import { useEffect, useRef, useState } from "react";
import type { Traduttore } from "../../i18n/index.ts";
import { testoErrore, useT } from "../lib/i18n.tsx";
import { streamEvents } from "../lib/sse.ts";
import { useQuery } from "../lib/hooks.ts";
import { linkProps } from "../lib/router.ts";
import { dataSqlite, filtraStack, statoStack } from "../lib/stato-stack.ts";
import type { StatoStack } from "../lib/stato-stack.ts";
import type { DeployEvent, Session, StackConRiepilogo } from "../lib/types.ts";
import { Badge, Button, ErrorNotice, Input, Spinner } from "../components/ui.tsx";

const STATI: StatoStack[] = ["non-avviato", "fermo", "attivo", "da-applicare", "errore"];

const ETICHETTA_STATO = {
  "non-avviato": { chiave: "stacks.nonAvviato", tono: "neutral" },
  fermo: { chiave: "stacks.fermo", tono: "neutral" },
  attivo: { chiave: "stacks.attivo", tono: "success" },
  "da-applicare": { chiave: "stacks.daApplicare", tono: "warning" },
  errore: { chiave: "stacks.errore", tono: "danger" },
} as const;

/** Ogni esito con una parola, non solo un simbolo: in corso e annullato non si confondono. */
export const ESITO_RILASCIO = {
  succeeded: "storico.esitoRiuscita",
  failed: "storico.esitoFallita",
  running: "storico.esitoInCorso",
  cancelled: "storico.esitoAnnullata",
} as const;

const CLASSE_PRIMARIO = "rounded-lg bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700";
const CLASSE_SECONDARIO =
  "superficie rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-[var(--superficie-tenue)]";

/**
 * Elenco degli stack, in tabella compatta con ricerca e filtro per stato.
 * Sotto `sm` restano visibili solo nome e stato.
 */
export function StacksPage({ sessione }: { sessione: Session }) {
  const { data, error, loading, reload } = useQuery<{ stacks: StackConRiepilogo[] }>("/stacks");
  const puòScrivere = sessione.permissions.includes("stack:write");
  const puòCambiareTag = puòScrivere && sessione.permissions.includes("stack:deploy");
  const t = useT();
  const [testo, setTesto] = useState("");
  const [stato, setStato] = useState<StatoStack | "tutti">("tutti");

  const visibili = data ? filtraStack(data.stacks, testo, stato) : [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{t.t("stacks.titolo")}</h1>
        <p className="text-sm tenue">{t.t("stacks.intro")}</p>
      </div>

      <ErrorNotice error={error} />

      {loading && <Spinner className="tenue" />}

      {data && data.stacks.length === 0 && (
        <div className="rounded-xl border border-dashed px-6 py-12 text-center">
          <p className="font-medium">{t.t("stacks.vuotoCosaFa")}</p>
          <p className="mt-1 text-sm tenue">{t.t("stacks.vuotoCosaFa2")}</p>
          {puòScrivere && (
            <div className="mt-4 flex justify-center gap-2">
              <a {...linkProps("/stacks/nuovo")} className={CLASSE_PRIMARIO}>
                {t.t("stacks.nuovo")}
              </a>
              <a {...linkProps("/importazione")} className={CLASSE_SECONDARIO}>
                {t.t("stacks.importaEsistente")}
              </a>
            </div>
          )}
        </div>
      )}

      {data && data.stacks.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="search"
              className="min-w-0 flex-1 sm:max-w-xs"
              placeholder={t.t("stacks.cerca")}
              aria-label={t.t("stacks.cerca")}
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
            />
            <select
              aria-label={t.t("stacks.filtroStato")}
              value={stato}
              onChange={(e) => setStato(e.target.value as StatoStack | "tutti")}
              className="rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm"
            >
              <option value="tutti">{t.t("stacks.tuttiStati")}</option>
              {STATI.map((s) => (
                <option key={s} value={s}>
                  {t.t(ETICHETTA_STATO[s].chiave)}
                </option>
              ))}
            </select>
            {puòScrivere && (
              <div className="ml-auto flex items-center gap-2">
                <a {...linkProps("/importazione")} className={CLASSE_SECONDARIO}>
                  {t.t("stacks.importa")}
                </a>
                <a {...linkProps("/stacks/nuovo")} className={CLASSE_PRIMARIO}>
                  {t.t("stacks.nuovo")}
                </a>
              </div>
            )}
          </div>

          {visibili.length === 0 ? (
            <div className="rounded-xl border border-dashed px-6 py-12 text-center">
              <p className="font-medium">{t.t("stacks.nessunoCorrisponde")}</p>
              <Button
                className="mt-3"
                onClick={() => {
                  setTesto("");
                  setStato("tutti");
                }}
              >
                {t.t("stacks.azzeraFiltri")}
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full table-fixed text-sm">
                <thead className="superficie text-left text-xs tenue">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t.t("stacks.colStack")}</th>
                    <th className="w-36 px-4 py-2 font-medium">{t.t("stacks.colStato")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("stacks.colStrategia")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("stacks.colDominio")}</th>
                    <th className="hidden w-24 px-4 py-2 font-medium sm:table-cell">{t.t("stacks.colContainer")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("stacks.colTag")}</th>
                    <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("stacks.colRilascio")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibili.map((stack) => (
                    <RigaStack key={stack.id} stack={stack} puòCambiareTag={puòCambiareTag} onTag={reload} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RigaStack({
  stack,
  puòCambiareTag,
  onTag,
}: {
  stack: StackConRiepilogo;
  puòCambiareTag: boolean;
  onTag: () => void;
}) {
  const t = useT();
  const { domini, container, tag, ultimoRilascio } = stack.riepilogo;
  return (
    <tr className="relative border-t hover:bg-[var(--superficie-tenue)]">
      <td className="px-4 py-2.5">
        {/* Il link copre l'intera riga con `after`, ma il testo resta selezionabile. */}
        <a
          {...linkProps(`/stacks/${stack.id}`)}
          className="block truncate font-medium after:absolute after:inset-0"
        >
          {stack.name}
        </a>
        {stack.description && <p className="truncate text-xs tenue">{stack.description}</p>}
      </td>
      <td className="px-4 py-2.5">
        <BadgeStato stato={statoStack(stack)} />
      </td>
      <td className="hidden truncate px-4 py-2.5 sm:table-cell">
        {t.t(stack.deployStrategy === "blue-green" ? "stacks.blueGreen" : "stacks.ricreazione")}
      </td>
      <td className="hidden truncate px-4 py-2.5 sm:table-cell">
        {testoDominio(domini)}
      </td>
      <td className="hidden px-4 py-2.5 sm:table-cell">
        {container ? `${container.sani}/${container.totali}` : "—"}
      </td>
      <td className="hidden px-4 py-2.5 font-mono text-xs sm:table-cell">
        {puòCambiareTag ? (
          <CellaTag stackId={stack.id} nome={stack.name} tag={tag} onFatto={onTag} />
        ) : (
          <span className="block truncate" title={tag ?? undefined}>{tag ?? "—"}</span>
        )}
      </td>
      <td className="hidden truncate px-4 py-2.5 sm:table-cell">
        {ultimoRilascio
          ? [
              t.t(ESITO_RILASCIO[ultimoRilascio.esito]),
              ultimoRilascio.revisione !== null && t.t("stacks.revisione", { n: ultimoRilascio.revisione }),
              t.tempoFa(dataSqlite(ultimoRilascio.quando)),
            ]
              .filter(Boolean)
              .join(" ")
          : "—"}
      </td>
    </tr>
  );
}

/**
 * Il tag, cambiabile dall'elenco: dopo la conferma salva una revisione con la
 * variabile del tag e rilascia, come il webhook. Della console resta l'esito.
 */
function CellaTag({
  stackId,
  nome,
  tag,
  onFatto,
}: {
  stackId: number;
  nome: string;
  tag: string | null;
  onFatto: () => void;
}) {
  const t = useT();
  const [aperta, setAperta] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<{ ok: boolean; testo: string } | null>(null);

  const rilascia = async (nuovo: string) => {
    setAperta(false);
    setInCorso(true);
    setEsito(null);
    try {
      let errore: string | null = t.t("stacks.tagSenzaEsito");
      for await (const { data } of streamEvents<DeployEvent>(`/stacks/${stackId}/tag/${encodeURIComponent(nuovo)}`)) {
        if (data.type === "finished") errore = null;
        else if (data.type === "failed") errore = data.error;
      }
      setEsito(errore === null ? { ok: true, testo: t.t("stacks.tagAggiornato", { tag: nuovo }) } : { ok: false, testo: errore });
    } catch (cause) {
      setEsito({ ok: false, testo: testoErrore(cause, t) });
    } finally {
      setInCorso(false);
      onFatto();
    }
  };

  // Sopra il link che copre la riga: un clic qui non apre lo stack.
  return (
    <div className="relative z-10 flex items-center gap-1.5">
      <button
        type="button"
        className="min-w-0 truncate rounded px-1 -mx-1 text-left hover:bg-[var(--superficie)] hover:underline disabled:no-underline"
        title={t.t("stacks.modificaTag")}
        disabled={inCorso}
        onClick={() => setAperta(true)}
      >
        {tag ?? "—"}
      </button>
      {inCorso && <Spinner className="shrink-0 tenue" />}
      {esito && !inCorso && (
        <span
          role="status"
          title={esito.testo}
          aria-label={esito.testo}
          className={esito.ok ? "shrink-0 text-emerald-600" : "shrink-0 text-red-600"}
        >
          {esito.ok ? "✓" : "✕"}
        </span>
      )}
      {aperta && (
        <FinestraTag stackId={stackId} nome={nome} tag={tag} onConferma={rilascia} onChiudi={() => setAperta(false)} />
      )}
    </div>
  );
}

function FinestraTag({
  stackId,
  nome,
  tag,
  onConferma,
  onChiudi,
}: {
  stackId: number;
  nome: string;
  tag: string | null;
  onConferma: (tag: string) => void;
  onChiudi: () => void;
}) {
  const t = useT();
  const finestra = useRef<HTMLDialogElement>(null);
  const [bozza, setBozza] = useState(tag ?? "");
  const storico = useQuery<{ variabile: string; precedenti: string[] }>(`/stacks/${stackId}/tag`);
  const nuovo = bozza.trim();

  // `showModal` dà gratis sfondo, focus intrappolato ed Esc per chiudere.
  useEffect(() => {
    if (!finestra.current?.open) finestra.current?.showModal();
  }, []);

  return (
    <dialog
      ref={finestra}
      onClose={onChiudi}
      className="superficie m-auto w-[calc(100%-2rem)] max-w-md rounded-xl border p-5 font-sans text-[var(--testo)] backdrop:bg-black/40"
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (nuovo && nuovo !== tag) onConferma(nuovo);
        }}
      >
        <h2 className="text-base font-semibold">{t.t("stacks.tagTitolo", { stack: nome })}</h2>
        <p className="mt-2 text-sm tenue">
          {t.t("stacks.tagTesto", { variabile: storico.data?.variabile ?? "TAG" })}
        </p>

        <Input
          autoFocus
          className="mt-4 font-mono"
          aria-label={t.t("stacks.modificaTag")}
          value={bozza}
          onChange={(e) => setBozza(e.target.value)}
        />

        {storico.data && storico.data.precedenti.length > 0 && (
          <div className="mt-4">
            <p className="text-xs tenue">{t.t("stacks.tagPrecedenti")}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {storico.data.precedenti.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setBozza(p)}
                  className={
                    "rounded-md border px-2 py-0.5 font-mono text-xs hover:bg-[var(--superficie-tenue)]" +
                    (p === nuovo ? " border-accent-600" : "")
                  }
                >
                  {p}
                  {p === tag && <span className="tenue"> · {t.t("stacks.tagInUso")}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        {storico.loading && <Spinner className="mt-4 tenue" />}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => finestra.current?.close()}>
            {t.t("comune.annulla")}
          </Button>
          <Button type="submit" variant="primary" disabled={!nuovo || nuovo === tag}>
            {t.t("stacks.tagConferma", { tag: nuovo })}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

/** `dettaglio`, se c'è, segue lo stato nella stessa pillola: «attivo · blue». */
export function BadgeStato({ stato, dettaglio }: { stato: StatoStack; dettaglio?: string }) {
  const t = useT();
  const { chiave, tono } = ETICHETTA_STATO[stato];
  // Una stringa sola: il Badge è flex e separerebbe i due pezzi col suo gap.
  return <Badge tone={tono}>{dettaglio ? `${t.t(chiave)} · ${dettaglio}` : t.t(chiave)}</Badge>;
}

/** Il primo dominio e quanti altri ce ne sono: nell'elenco e nella striscia del dettaglio. */
export function testoDominio(domini: readonly string[]): string {
  return domini.length === 0 ? "—" : domini.length === 1 ? domini[0]! : `${domini[0]} +${domini.length - 1}`;
}

export function formatoData(iso: string, t: Traduttore): string {
  return t.data(dataSqlite(iso));
}
